import { useState } from 'react';
import { DetailViewCard } from '../../Compounds/CardWithTitle/DetailViewCard';
import { DetailPanelParams } from '../../Compounds/DetailPanel/DetailPanel';
import { RpmAccount, updateRpmAccount } from '../../../store/RpmAccount';
import { DetailsLinkCTA } from '../../Compounds/DetailsLinkCTA';
import { EditFieldType, LabelValueField } from '../../Compounds/DetailPanel/LabelValueField';
import { isAnalyst, isData, isObserver } from '../../../store/User';
import { DetailPanelContainer } from '../../Compounds/DetailPanel/DetailPanelContainer';
import { AssetMiniTable } from '../../Compounds/Asset/AssetMiniTable';
import { NewLinkDialog } from '../../Compounds/Linking/NewLinkDialog/NewLinkDialog';
import { getFormattedDate } from '../../../utils/helpers';
import { ProvisionalAssetMiniTable } from '../../Compounds/ProvisionalAsset/ProvisionalAssetMiniTable';
import { RPMAcctRunTotals } from '../../Compounds/RunTotal/RPMAcctRunTotals';
import { TicketMiniTable } from '../../Compounds/Ticket/TicketMiniTable';
import { fetchTicketsByRpmAcctId } from '../../../store/Ticket';
import {
  fetchAssetsByRpmAcctId,
  getAssetsByRpmAcctLoadingDrawerSubject,
  getAssetsByRpmAcctDrawerSub
} from '../../../store/Asset';
import { deleteLink } from '../../../store/Link';
import {
  fetchProvisionalAssetsByRpmAcctId,
  getProvisionalAssetsByRpmAcctLoadingDrawerSubject,
  getProvisionalAssetsByRpmAcctDrawerSub
} from '../../../store/ProvisionalAsset';
import { DetailsComments } from '../../Compounds/DetailsComments';
import { EntityType, TypeAsset } from '../../../store/Search';
import { DeleteRpmAccountBtn } from './DeleteRpmAccountBtn';
import { clearDrawerState } from '../../../store/Drawer';

export interface RPMAccountDetailsParams {
  data: RpmAccount;
}

export const rpmSections = (data: RpmAccount): DetailPanelParams[] => [
  {
    data,
    canEdit: [],
    fieldMap: {
      ultimateParent: 'SFDC Ultimate Parent',
      accountCustomerName: 'RPM Customer',
      supplier: 'RPM Supplier',
      source: 'Source',
      address: 'Address',
      circuitId: 'Circuit ID'
    },
    order: ['ultimateParent', 'accountCustomerName', 'supplier', 'source', 'address', 'circuitId'],
    links: [{ field: 'ultimateParent', link: `/customer/${data.ultimateParentId}`, external: false }]
  },
  {
    data,
    canEdit: [
      {
        field: 'assignedRep',
        type: EditFieldType.TEXT
      }
    ],
    fieldMap: {
      assignedAgency: 'Assigned Agency',
      assignedRep: 'Assigned Rep',
      reportedAgency: 'Reported Agency'
    },
    order: ['assignedAgency', 'assignedRep', 'reportedAgency']
  },
  {
    data,
    canEdit: [],
    fieldMap: {
      firstCommissionRun: 'First Commission Run',
      lastCommissionRun: 'Last Commission Run',
      cancelDate: 'Cancel Date',
      disconnectDate: 'Disconnect Date',
      accountGroup: 'Account Group',
      notesFromTsb: 'Notes from TSD'
    },
    order: ['firstCommissionRun', 'lastCommissionRun', 'cancelDate', 'disconnectDate', 'accountGroup', 'notesFromTsb']
  }
];

export const RPMAccountDetails = ({ data }: RPMAccountDetailsParams) => {
  const [isLinkFormOpen, setIsLinkFormOpen] = useState<boolean>(false);
  const [typeAsset, setTypeAsset] = useState<TypeAsset>(EntityType.ASSET);

  const cardActions = (typeAsset: TypeAsset) => {
    return isObserver() || isData() ? (
      <></>
    ) : (
      <DetailsLinkCTA
        setIsFormOpen={() => {
          setIsLinkFormOpen(true);
          setTypeAsset(typeAsset);
        }}
      />
    );
  };

  const saveAction = (update: RpmAccount) => updateRpmAccount(update, true);

  const deleteButton = () => {
    if (
      isObserver() ||
      isAnalyst() ||
      isData() ||
      (data.linkCount !== null && Number(data.linkCount) > 0) ||
      (data.ticketCount !== null && Number(data.ticketCount) > 0)
    )
      return <></>;

    return (
      <DeleteRpmAccountBtn
        accountId={data.accountId}
        onDelete={() => clearDrawerState()}
      />
    );
  };

  return (
    <div
      className="space-y-4"
      data-testid="rpm-account-details-drawer">
      <DetailViewCard
        enableCollapse
        key="commissions_detail"
        title="Commissions">
        <RPMAcctRunTotals rpmAcctId={data.accountId} />
      </DetailViewCard>
      <DetailViewCard
        enableCollapse
        key="rpm-account-detail-card"
        title="Info">
        <div className="min-h-[15rem]">
          <DetailPanelContainer
            data={rpmSections(data)}
            saveAction={(d) => saveAction(d as RpmAccount)}
          />
        </div>
      </DetailViewCard>
      <TicketMiniTable
        id={data.accountId}
        fetchFunction={fetchTicketsByRpmAcctId}
        association={{
          type: 'rmpAccount',
          body: {
            entityId: String(data.accountId)
          }
        }}
        showCreate={true}
        objectType="rpmAccount"
        objectData={data}
        objectLinkId={data.accountId}
        objectLinkDisplay={`RPM Account ${data.accountNumber}`}
        drawer
      />
      <DetailsComments
        id={data.accountId}
        entityType="rpm_account"
      />
      <DetailViewCard
        cardActions={cardActions(EntityType.ASSET)}
        enableCollapse
        key="asset_table"
        title="Assets">
        <AssetMiniTable
          id={data.accountId}
          fetchFunction={fetchAssetsByRpmAcctId}
          removeFunction={deleteLink}
          dataSubject={getAssetsByRpmAcctDrawerSub}
          loadingSubject={getAssetsByRpmAcctLoadingDrawerSubject}
          drawer
          showRemove
        />
      </DetailViewCard>
      <DetailViewCard
        cardActions={cardActions(EntityType.PROVISIONAL_ASSET)}
        enableCollapse
        key="provisional_asset_table"
        title="Provisional Assets">
        <ProvisionalAssetMiniTable
          id={data.accountId}
          fetchFunction={fetchProvisionalAssetsByRpmAcctId}
          removeFunction={deleteLink}
          dataSubject={getProvisionalAssetsByRpmAcctDrawerSub}
          loadingSubject={getProvisionalAssetsByRpmAcctLoadingDrawerSubject}
          drawer
          showRemove
        />
      </DetailViewCard>
      <DetailViewCard
        enableCollapse
        key="meta_detail"
        title="Meta">
        <div className="flex px-4 pt-4 w-full items-center">
          <LabelValueField
            label="RPM Account ID"
            value={data.accountId}
            field="accountId"
          />
          <LabelValueField
            label="SFDC SR #"
            value={data.serialNumber || ''}
            field="serialNumber"
          />
        </div>
        <div className="px-4">
          <LabelValueField
            label="Added"
            value={getFormattedDate(data.addedDate)}
            field="addedDate"
          />
        </div>
        {deleteButton()}
      </DetailViewCard>
      <NewLinkDialog
        open={isLinkFormOpen}
        setOpen={setIsLinkFormOpen}
        rpmAccount={data}
        typeAsset={typeAsset}
      />
    </div>
  );
};
